import React, { useCallback, useEffect, useRef, useState } from 'react';
import BackgroundAudioPlayer from './BackgroundAudioPlayer';

const formatDuration = (seconds) => {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

export default React.memo(function QuizProgressTracker({
    quizStarted,
    isLastMinute,
    timeLeft,
    durationMinutes,
    onDurationChange,
    onStartQuiz,
    timeConfigError,
    questions,
    hasAnswerForQuestion,
    pageStartIndex,
    currentRangeEnd,
    onJumpToQuestion,
    onRetakeQuiz,
    onSubmitQuiz,
    progress
}) {
    const panelRef = useRef(null);
    const dragStateRef = useRef({
        active: false,
        offsetX: 0,
        offsetY: 0
    });

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [panelPosition, setPanelPosition] = useState({ x: null, y: 84 });

    const clampPosition = useCallback((x, y) => {
        const panel = panelRef.current;
        if (!panel) {
            return { x, y };
        }

        const maxX = Math.max(8, window.innerWidth - panel.offsetWidth - 8);
        const maxY = Math.max(8, window.innerHeight - panel.offsetHeight - 8);

        return {
            x: Math.min(Math.max(8, x), maxX),
            y: Math.min(Math.max(8, y), maxY)
        };
    }, []);

    const handleDragStart = useCallback((event) => {
        if (window.innerWidth <= 992) {
            return;
        }

        const panel = panelRef.current;
        if (!panel) {
            return;
        }

        const rect = panel.getBoundingClientRect();
        dragStateRef.current = {
            active: true,
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top
        };

        document.body.style.userSelect = 'none';
    }, []);

    const handleDragMove = useCallback((event) => {
        if (!dragStateRef.current.active || window.innerWidth <= 992) {
            return;
        }

        const nextX = event.clientX - dragStateRef.current.offsetX;
        const nextY = event.clientY - dragStateRef.current.offsetY;
        setPanelPosition(clampPosition(nextX, nextY));
    }, [clampPosition]);

    const handleDragEnd = useCallback(() => {
        if (!dragStateRef.current.active) {
            return;
        }

        dragStateRef.current.active = false;
        document.body.style.userSelect = '';
    }, []);

    useEffect(() => {
        window.addEventListener('mousemove', handleDragMove);
        window.addEventListener('mouseup', handleDragEnd);

        return () => {
            window.removeEventListener('mousemove', handleDragMove);
            window.removeEventListener('mouseup', handleDragEnd);
            document.body.style.userSelect = '';
        };
    }, [handleDragEnd, handleDragMove]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 992) {
                return;
            }

            if (panelPosition.x === null) {
                return;
            }

            setPanelPosition((prev) => clampPosition(prev.x, prev.y));
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [clampPosition, panelPosition.x]);

    const panelStyle = panelPosition.x === null
        ? { right: '16px', top: `${panelPosition.y}px`, left: 'auto' }
        : { left: `${panelPosition.x}px`, top: `${panelPosition.y}px`, right: 'auto' };

    return (
        <div
            ref={panelRef}
            className={`quiz-floating-panel end-0 me-3 ${isCollapsed ? 'is-collapsed' : ''}`}
            style={panelStyle}
        >
            <div className="quiz-panel-toolbar" onMouseDown={handleDragStart}>
                <div className="quiz-toolbar-title">
                    <i className="fas fa-grip-lines me-2"></i>
                    Bảng luyện tập
                </div>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={() => setIsCollapsed((prev) => !prev)}
                >
                    <i className={`fas ${isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'} me-1`}></i>
                    {isCollapsed ? 'Mở rộng' : 'Thu gọn'}
                </button>
            </div>

            <div
                className="header-quiz alert alert-light border mb-1"
                role="alert"
            >
                <div className="d-flex justify-content-between align-items-center mb-0">
                    <h5 className="mb-0">
                        <i className="fas fa-clipboard-list me-2"></i>
                        Phòng luyện tập
                    </h5>
                    {quizStarted ? (
                        <div className={`badge ${isLastMinute ? 'bg-danger' : 'bg-dark'} px-3 py-2`}>
                            <i className={`fas ${isLastMinute ? 'fa-hourglass-end' : 'fa-stopwatch'} me-2`}></i>
                            {formatDuration(timeLeft)}
                        </div>
                    ) : (
                        <div className="badge bg-secondary px-3 py-2">
                            <i className="fas fa-eye me-2"></i>
                            Xem trước đề
                        </div>
                    )}
                </div>

                {!isCollapsed && (
                    <>
                            <small className="text-muted d-block">
                                Hãy luyện tập bằng cách rút ngắn thời gian làm bài, giúp bạn cải thiện tốc độ tư duy và phản xạ khi làm bài thi thật.
                            </small>
                        <div className="d-flex flex-row justify-content-between align-items-md-center gap-1 mt-2">
                            {/* <div className='d-flex flex-row'>
                                <div className="fw-semibold mb-1">
                                    <i className="fas fa-hourglass-start text-warning me-2"></i>
                                    Thiết lập thời gian luyện tập
                                </div>
                                </div> */}

                            <div className="d-flex flex-column flex-sm-row align-items-stretch gap-2 quiz-time-controls">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="fas fa-stopwatch"></i>
                                    </span>
                                    <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        className="form-control"
                                        value={durationMinutes}
                                        onChange={(event) => onDurationChange(event.target.value)}
                                        disabled={quizStarted}
                                    />
                                    <span className="input-group-text">phút</span>
                                </div>

                                <button
                                    className="btn btn-primary"
                                    onClick={onStartQuiz}
                                    disabled={quizStarted}
                                >
                                    <i className={`fas ${quizStarted ? 'fa-check' : 'fa-stopwatch'} me-2`}></i>
                                    {quizStarted ? 'Đang làm bài' : 'Bắt đầu tính giờ'}
                                </button>
                            </div>
                        </div>

                        {timeConfigError && (
                            <div className="alert alert-danger py-2 mt-3 mb-0" role="alert">
                                <small>{timeConfigError}</small>
                            </div>
                        )}

                        <BackgroundAudioPlayer
                            audioUrl="https://data.linklearn.vn/nhacnen02.mp3"
                            title="Nhạc nền luyện tập"
                            defaultVolume={0.5}
                        />

                        <div className="pt-1 border-top">
                            <p className="mb-2 text-muted"><small>Chuyển nhanh đến câu:</small></p>
                            <div className="d-flex flex-wrap gap-1">
                                {questions.map((question, index) => {
                                    const hasAnswer = hasAnswerForQuestion(question);
                                    const isVisibleInCurrentPage = index >= pageStartIndex && index < currentRangeEnd;

                                    return (
                                        <button
                                            key={question.id}
                                            className={`quiz-jump-button btn btn-sm ${
                                                hasAnswer
                                                    ? 'btn-success'
                                                    : isVisibleInCurrentPage
                                                    ? 'btn-primary'
                                                    : 'btn-outline-secondary'
                                            }`}
                                            onClick={() => onJumpToQuestion(index)}
                                            title={hasAnswer ? 'Đã trả lời' : 'Chưa trả lời'}
                                        >
                                            {index + 1}
                                            {question.questionType === 'multiple' && (
                                                <i className="fas fa-check-double quiz-jump-multiple-icon"></i>
                                            )}
                                        </button>
                                    );
                                })}

                                <div className="ms-auto d-flex gap-2">
                                    <button
                                        className="btn btn-primary btn-lg"
                                        onClick={onRetakeQuiz}
                                    >
                                        <i className="fas fa-redo me-2"></i>
                                        Làm lại bài thi
                                    </button>
                                    <button
                                        className="btn btn-success p-1"
                                        onClick={onSubmitQuiz}
                                    >
                                        <i className="fas fa-check me-2"></i>
                                        Nộp bài
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            {!isCollapsed && (
                <div className="mb-2">
                    <div className="d-flex justify-content-between mb-2">
                        <span>
                            <strong>Câu {pageStartIndex + 1}-{currentRangeEnd}</strong>/{questions.length}
                        </span>
                        <span className="text-muted">{Math.round(progress)}%</span>
                    </div>
                    <div className="progress quiz-progress">
                        <div
                            className="progress-bar bg-success"
                            style={{ width: `${progress}%` }}
                            role="progressbar"
                        />
                    </div>
                </div>
            )}
            </div>

        </div>
    );
});